import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Users, Download } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatErr = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
};

const inputCls =
  "w-full rounded-xl border border-[rgba(163,74,107,0.22)] bg-white px-4 py-3 text-sm text-cream placeholder:text-mutedlilac outline-none transition-colors duration-300 focus:border-[#A34A6B]/60";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
  ", " +
  new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

const buildCsv = (leads) => {
  const header = ["Name", "Email", "Would like to gain", "Reference", "Joined"];
  const rows = leads.map((l) => [l.first_name, l.email, l.gain || "", l.id.slice(0, 8).toUpperCase(), fmtDate(l.created_at)]);
  return [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
};

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { withCredentials: true });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) setAuth(false);
      else toast.error("Could not load reservations.");
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAuth(data);
        return;
      } catch {
        // try refresh below
      }
      try {
        const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        setAuth(data);
      } catch {
        setAuth(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (auth) loadLeads();
  }, [auth, loadLeads]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setAuth(data);
      toast.success(`Welcome back, ${data.name}.`);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const doLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setAuth(false);
    setLeads([]);
  };

  const exportCsv = () => {
    const blob = new Blob([buildCsv(leads)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `menopause-reclaim-reservations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Reservation list downloaded.");
  };

  if (auth === null) {
    return (
      <main data-testid="admin-page" className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="h-6 w-6 animate-spin text-[#A34A6B]" />
      </main>
    );
  }

  if (auth === false) {
    return (
      <main data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-[#F9F3EE] px-6">
        <div className="w-full max-w-sm rounded-3xl border border-[rgba(163,74,107,0.18)] bg-white/85 p-8 shadow-[0_24px_70px_rgba(163,74,107,0.12)] sm:p-10">
          <Logo compact />
          <h1 className="mt-8 font-serif text-2xl text-cream">Reservations review</h1>
          <p className="mt-2 text-sm text-lilac">Sign in to see everyone who has joined.</p>
          <form onSubmit={submitLogin} className="mt-8 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              className={inputCls}
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              className={inputCls}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            {error ? (
              <p data-testid="admin-login-error" className="text-xs font-medium text-[#A34A6B]">{error}</p>
            ) : null}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#A34A6B] px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDF6F2] transition-[background-color,transform] duration-300 hover:bg-[#8E3D5B] active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Sign in
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-ink">
      <div className="border-b border-[rgba(163,74,107,0.18)] bg-[#F4E8E4]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-5 sm:px-10">
          <Logo compact />
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 rounded-full border border-[#A34A6B]/30 bg-white px-4 py-1.5 text-xs text-cream">
              <Users className="h-3.5 w-3.5 text-[#A34A6B]" />
              <span data-testid="leads-count">{leads.length}</span>&nbsp;reservations
            </span>
            <button
              onClick={exportCsv}
              data-testid="export-csv-button"
              disabled={leads.length === 0}
              className="flex items-center gap-2 rounded-full bg-[#A34A6B] px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#FDF6F2] transition-colors duration-300 hover:bg-[#8E3D5B] disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
            <button
              onClick={loadLeads}
              data-testid="leads-refresh-button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#A34A6B]/30 bg-white text-[#A34A6B] transition-colors duration-300 hover:bg-[#F9F3EE]"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loadingLeads ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={doLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-[#A34A6B]/30 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#A34A6B] transition-colors duration-300 hover:bg-[#F9F3EE]"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-12 sm:px-10">
        <h1 className="font-serif text-2xl text-cream sm:text-3xl">
          Priority <span className="italic text-[#A34A6B]">reservations</span>
        </h1>
        <p className="mt-2 text-sm text-lilac">Newest first. Signed in as {auth.email}.</p>

        <div className="mt-8 overflow-hidden rounded-3xl border border-[rgba(163,74,107,0.18)] bg-white/85 shadow-[0_24px_70px_rgba(163,74,107,0.08)]">
          {loadingLeads && leads.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-[#A34A6B]" />
            </div>
          ) : leads.length === 0 ? (
            <p data-testid="leads-empty" className="py-20 text-center text-sm text-lilac">No reservations yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="leads-table" className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[rgba(163,74,107,0.15)] text-[10px] uppercase tracking-[0.2em] text-[#A34A6B]">
                    <th className="px-6 py-4 font-semibold">Name</th>
                    <th className="px-6 py-4 font-semibold">Email</th>
                    <th className="px-6 py-4 font-semibold">Would like to gain</th>
                    <th className="px-6 py-4 font-semibold">Ref</th>
                    <th className="px-6 py-4 font-semibold">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id.slice(0, 8)}`} className="border-b border-[rgba(163,74,107,0.08)] transition-colors duration-200 last:border-0 hover:bg-[#F9F3EE]/60">
                      <td className="px-6 py-4 font-medium text-cream">{l.first_name}</td>
                      <td className="px-6 py-4 text-lilac">{l.email}</td>
                      <td className="max-w-xs px-6 py-4 text-lilac">{l.gain || <span className="text-mutedlilac">—</span>}</td>
                      <td className="px-6 py-4 font-mono text-xs text-[#A34A6B]">{l.id.slice(0, 8).toUpperCase()}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-xs text-mutedlilac">{fmtDate(l.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
